import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calculator, Copy, Trash2, TrendingUp, CheckCircle2, ArrowRight, Coins, PiggyBank, Target, DollarSign, BarChart3 } from 'lucide-react';
import CryptoAffiliateBlock from '../../CryptoAffiliateBlock';

const CryptoInvestmentCalculatorTool = () => {
  const [investmentAmount, setInvestmentAmount] = useState('');
  const [cryptoPrice, setCryptoPrice] = useState('');
  const [targetPrice, setTargetPrice] = useState('');
  const [monthlyDca, setMonthlyDca] = useState('');
  const [dcaMonths, setDcaMonths] = useState('');
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState('');

  useEffect(() => {
    calculate();
  }, [investmentAmount, cryptoPrice, targetPrice, monthlyDca, dcaMonths]);

  const calculate = () => {
    const investment = parseFloat(investmentAmount);
    const price = parseFloat(cryptoPrice);
    const target = parseFloat(targetPrice);
    const monthly = parseFloat(monthlyDca) || 0;
    const months = parseFloat(dcaMonths) || 0;

    if (isNaN(investment) || isNaN(price) || price <= 0 || investment <= 0) {
      setResult(null);
      return;
    }

    // Calculate coins from initial investment
    const coinsFromInitial = investment / price;
    
    // Calculate DCA contributions
    const totalDcaInvestment = monthly * months;
    const avgDcaPrice = price; // Simplified - in reality would vary
    const coinsFromDca = totalDcaInvestment / avgDcaPrice;
    
    // Totals
    const totalCoins = coinsFromInitial + coinsFromDca;
    const totalInvestment = investment + totalDcaInvestment;
    const currentValue = totalCoins * price;
    
    // Target calculations (if target price provided)
    let targetValue = 0;
    let targetProfit = 0;
    let targetRoi = 0;
    let targetMultiplier = 0;
    let priceIncreaseNeeded = 0;
    
    if (target > 0) {
      targetValue = totalCoins * target;
      targetProfit = targetValue - totalInvestment;
      targetRoi = ((targetValue - totalInvestment) / totalInvestment) * 100;
      targetMultiplier = targetValue / totalInvestment;
      priceIncreaseNeeded = ((target - price) / price) * 100;
    }

    // Break-even analysis
    const breakEvenPrice = totalInvestment / totalCoins;

    setResult({
      coinsFromInitial,
      coinsFromDca,
      totalCoins,
      totalInvestment,
      totalDcaInvestment,
      currentValue,
      targetValue,
      targetProfit,
      targetRoi,
      targetMultiplier,
      priceIncreaseNeeded,
      breakEvenPrice,
      hasTarget: target > 0,
      hasDca: monthly > 0 && months > 0
    });
  };

  const copyResult = (value, label) => {
    navigator.clipboard.writeText(typeof value === 'number' ? value.toFixed(6) : value);
    setCopied(label);
    setTimeout(() => setCopied(''), 2000);
  };

  const clearAll = () => {
    setInvestmentAmount('');
    setCryptoPrice('');
    setTargetPrice('');
    setMonthlyDca('');
    setDcaMonths('');
    setResult(null);
  };

  const formatCurrency = (value) => '$' + value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const formatPercent = (value) => (value >= 0 ? '+' : '') + value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '%';
  const formatCoins = (value) => value.toLocaleString(undefined, { minimumFractionDigits: 6, maximumFractionDigits: 6 });

  const scenarios = [
    { label: 'BTC $10K @ $50K', investment: '10000', price: '50000', target: '100000', monthly: '', months: '' },
    { label: 'ETH $5K @ $2K + DCA', investment: '5000', price: '2000', target: '5000', monthly: '500', months: '12' },
    { label: 'SOL $1K @ $100', investment: '1000', price: '100', target: '300', monthly: '100', months: '6' },
  ];

  const applyScenario = (scenario) => {
    setInvestmentAmount(scenario.investment);
    setCryptoPrice(scenario.price);
    setTargetPrice(scenario.target);
    setMonthlyDca(scenario.monthly);
    setDcaMonths(scenario.months);
  };

  return (
    <div className="space-y-8" data-testid="crypto-investment-calculator">
      {/* Quick Examples */}
      <div className="flex flex-wrap gap-2">
        <span className="text-sm text-gray-400 mr-2">Quick Examples:</span>
        {scenarios.map((scenario, index) => (
          <button
            key={index}
            onClick={() => applyScenario(scenario)}
            className="px-3 py-1 text-xs bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-full text-gray-300 transition-colors"
            data-testid={`scenario-${index}`}
          >
            {scenario.label}
          </button>
        ))}
      </div>

      {/* Calculator Input Section */}
      <div className="p-6 bg-gray-800 rounded-xl space-y-5">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
            <PiggyBank size={22} className="text-green-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">Crypto Investment Calculator</h3>
            <p className="text-sm text-gray-400">Plan your cryptocurrency investment strategy</p>
          </div>
        </div>
        
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Initial Investment (USD) <span className="text-red-400">*</span>
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
              <input
                type="number"
                step="0.01"
                min="0"
                value={investmentAmount}
                onChange={(e) => setInvestmentAmount(e.target.value)}
                placeholder="e.g., 10000"
                className="w-full bg-gray-900 border border-gray-700 rounded-lg pl-8 pr-4 py-3 text-white placeholder-gray-500 focus:border-green-500 focus:outline-none text-lg"
                data-testid="investment-input"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">Lump sum investment amount</p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Current Crypto Price (USD) <span className="text-red-400">*</span>
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
              <input
                type="number"
                step="0.01"
                min="0"
                value={cryptoPrice}
                onChange={(e) => setCryptoPrice(e.target.value)}
                placeholder="e.g., 50000"
                className="w-full bg-gray-900 border border-gray-700 rounded-lg pl-8 pr-4 py-3 text-white placeholder-gray-500 focus:border-green-500 focus:outline-none text-lg"
                data-testid="price-input"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">Current price per coin</p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Target Price (USD) <span className="text-gray-500">(optional)</span>
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
              <input
                type="number"
                step="0.01"
                min="0"
                value={targetPrice}
                onChange={(e) => setTargetPrice(e.target.value)}
                placeholder="e.g., 100000"
                className="w-full bg-gray-900 border border-gray-700 rounded-lg pl-8 pr-4 py-3 text-white placeholder-gray-500 focus:border-green-500 focus:outline-none"
                data-testid="target-price-input"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">Your price target for profit calculation</p>
          </div>

          <div className="p-4 bg-gray-900/50 rounded-lg border border-gray-700">
            <label className="block text-sm font-medium text-gray-300 mb-3">
              DCA Strategy <span className="text-gray-500">(optional)</span>
            </label>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">$</span>
                  <input
                    type="number"
                    step="1"
                    min="0"
                    value={monthlyDca}
                    onChange={(e) => setMonthlyDca(e.target.value)}
                    placeholder="Monthly"
                    className="w-full bg-gray-900 border border-gray-700 rounded-lg pl-7 pr-3 py-2 text-white text-sm placeholder-gray-500 focus:border-green-500 focus:outline-none"
                    data-testid="monthly-dca-input"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">per month</p>
              </div>
              <div>
                <input
                  type="number"
                  step="1"
                  min="0"
                  value={dcaMonths}
                  onChange={(e) => setDcaMonths(e.target.value)}
                  placeholder="Months"
                  className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm placeholder-gray-500 focus:border-green-500 focus:outline-none"
                  data-testid="dca-months-input"
                />
                <p className="text-xs text-gray-500 mt-1">duration</p>
              </div>
            </div>
          </div>
        </div>

        <button
          onClick={clearAll}
          className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-gray-300 transition-colors text-sm"
          data-testid="clear-btn"
        >
          <Trash2 size={14} />
          Clear All
        </button>
      </div>

      {/* Results Section */}
      {result && (
        <div className="space-y-4">
          {/* Investment Summary */}
          <div className="p-6 bg-gradient-to-r from-green-500/10 to-blue-500/10 rounded-xl border border-green-500/30">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <Coins size={20} className="text-green-400" />
                Investment Summary
              </h3>
              <button
                onClick={() => copyResult(result.totalCoins, 'coins')}
                className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
              >
                {copied === 'coins' ? <CheckCircle2 size={18} className="text-green-400" /> : <Copy size={18} className="text-gray-400" />}
              </button>
            </div>
            
            <div className="text-center mb-4">
              <p className="text-gray-400 text-sm">Total Coins Acquired</p>
              <p className="text-4xl font-bold text-green-400">{formatCoins(result.totalCoins)}</p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
              <div className="text-center p-3 bg-gray-800/50 rounded-lg">
                <p className="text-xs text-gray-400 mb-1">Initial Investment</p>
                <p className="text-lg font-semibold text-white">{formatCurrency(parseFloat(investmentAmount) || 0)}</p>
              </div>
              {result.hasDca && (
                <div className="text-center p-3 bg-gray-800/50 rounded-lg">
                  <p className="text-xs text-gray-400 mb-1">DCA Total</p>
                  <p className="text-lg font-semibold text-blue-400">{formatCurrency(result.totalDcaInvestment)}</p>
                </div>
              )}
              <div className="text-center p-3 bg-gray-800/50 rounded-lg">
                <p className="text-xs text-gray-400 mb-1">Total Invested</p>
                <p className="text-lg font-semibold text-white">{formatCurrency(result.totalInvestment)}</p>
              </div>
              <div className="text-center p-3 bg-gray-800/50 rounded-lg">
                <p className="text-xs text-gray-400 mb-1">Current Value</p>
                <p className="text-lg font-semibold text-white">{formatCurrency(result.currentValue)}</p>
              </div>
            </div>
          </div>

          {/* Target Price Analysis */}
          {result.hasTarget && (
            <div className="p-6 bg-gray-800 rounded-xl">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Target size={20} className="text-yellow-400" />
                Target Price Analysis
              </h3>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="p-4 bg-green-500/10 rounded-lg border border-green-500/30">
                  <p className="text-sm text-gray-400 mb-1">Value at Target</p>
                  <p className="text-2xl font-bold text-green-400">{formatCurrency(result.targetValue)}</p>
                </div>
                <div className="p-4 bg-green-500/10 rounded-lg border border-green-500/30">
                  <p className="text-sm text-gray-400 mb-1">Potential Profit</p>
                  <p className="text-2xl font-bold text-green-400">{formatCurrency(result.targetProfit)}</p>
                </div>
                <div className="p-4 bg-blue-500/10 rounded-lg border border-blue-500/30">
                  <p className="text-sm text-gray-400 mb-1">ROI at Target</p>
                  <p className="text-2xl font-bold text-blue-400">{formatPercent(result.targetRoi)}</p>
                </div>
                <div className="p-4 bg-purple-500/10 rounded-lg border border-purple-500/30">
                  <p className="text-sm text-gray-400 mb-1">Growth Multiplier</p>
                  <p className="text-2xl font-bold text-purple-400">{result.targetMultiplier.toFixed(2)}x</p>
                </div>
              </div>

              <div className="mt-4 p-4 bg-gray-900/50 rounded-lg">
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Price increase needed to reach target:</span>
                  <span className="text-xl font-semibold text-yellow-400">{formatPercent(result.priceIncreaseNeeded)}</span>
                </div>
              </div>
            </div>
          )}

          {/* Break-Even Analysis */}
          <div className="p-4 bg-gray-800/50 rounded-lg border border-gray-700">
            <h4 className="text-sm font-medium text-gray-300 mb-3 flex items-center gap-2">
              <BarChart3 size={16} className="text-orange-400" />
              Break-Even Analysis
            </h4>
            <div className="flex justify-between items-center">
              <span className="text-gray-400">Your break-even price:</span>
              <span className="text-xl font-semibold text-orange-400">{formatCurrency(result.breakEvenPrice)}</span>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              The coin price must stay above {formatCurrency(result.breakEvenPrice)} for you to be profitable.
            </p>
          </div>

          {/* Calculation Details */}
          <div className="p-4 bg-gray-800/50 rounded-lg border border-gray-700">
            <h4 className="text-sm font-medium text-gray-300 mb-3 flex items-center gap-2">
              <Calculator size={16} className="text-green-400" />
              Calculation Breakdown
            </h4>
            <div className="space-y-2 text-sm font-mono">
              <p className="text-gray-400">
                Coins from initial = {formatCurrency(parseFloat(investmentAmount) || 0)} / {formatCurrency(parseFloat(cryptoPrice) || 0)} = <span className="text-green-400">{formatCoins(result.coinsFromInitial)}</span>
              </p>
              {result.hasDca && (
                <p className="text-gray-400">
                  Coins from DCA = {formatCurrency(result.totalDcaInvestment)} / {formatCurrency(parseFloat(cryptoPrice) || 0)} = <span className="text-blue-400">{formatCoins(result.coinsFromDca)}</span>
                </p>
              )}
              <p className="text-gray-400">
                Total Coins = {formatCoins(result.coinsFromInitial)} {result.hasDca ? `+ ${formatCoins(result.coinsFromDca)}` : ''} = <span className="text-white font-semibold">{formatCoins(result.totalCoins)}</span>
              </p>
            </div>
          </div>
          
          {/* Affiliate CTA Block */}
          <CryptoAffiliateBlock 
            profit={result.targetProfit} 
            cryptoName="crypto"
            toolName="Crypto Investment Calculator"
            toolSlug="crypto-investment-calculator"
          />
        </div>
      )}

      {/* Related Crypto Tools - Internal Links */}
      <div className="p-5 bg-gradient-to-r from-green-500/10 to-teal-500/10 rounded-xl border border-green-500/20">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Coins size={20} className="text-green-400" />
          Related Crypto Calculators
        </h3>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
          <Link
            to="/tools/bitcoin-profit-calculator"
            className="flex items-center justify-between p-4 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors group"
          >
            <div>
              <p className="font-medium text-white group-hover:text-orange-400 transition-colors">BTC Profit</p>
              <p className="text-sm text-gray-400">Bitcoin calculator</p>
            </div>
            <ArrowRight size={18} className="text-gray-500 group-hover:text-orange-400 transition-colors" />
          </Link>
          <Link
            to="/tools/ethereum-profit-calculator"
            className="flex items-center justify-between p-4 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors group"
          >
            <div>
              <p className="font-medium text-white group-hover:text-indigo-400 transition-colors">ETH Profit</p>
              <p className="text-sm text-gray-400">Ethereum calculator</p>
            </div>
            <ArrowRight size={18} className="text-gray-500 group-hover:text-indigo-400 transition-colors" />
          </Link>
          <Link
            to="/tools/crypto-gain-calculator"
            className="flex items-center justify-between p-4 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors group"
          >
            <div>
              <p className="font-medium text-white group-hover:text-green-400 transition-colors">Crypto Gains</p>
              <p className="text-sm text-gray-400">Track your gains</p>
            </div>
            <ArrowRight size={18} className="text-gray-500 group-hover:text-green-400 transition-colors" />
          </Link>
          <Link
            to="/tools/crypto-roi-calculator"
            className="flex items-center justify-between p-4 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors group"
          >
            <div>
              <p className="font-medium text-white group-hover:text-blue-400 transition-colors">Crypto ROI</p>
              <p className="text-sm text-gray-400">Return analysis</p>
            </div>
            <ArrowRight size={18} className="text-gray-500 group-hover:text-blue-400 transition-colors" />
          </Link>
        </div>
      </div>

      {/* Investment Strategies Section */}
      <div className="p-5 bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-xl border border-blue-500/20">
        <h4 className="text-lg font-semibold text-white mb-3">💡 Crypto Investment Strategies</h4>
        <ul className="space-y-2 text-gray-300">
          <li className="flex items-start gap-2">
            <span className="text-green-400 mt-1">•</span>
            <span><strong>Dollar-Cost Averaging (DCA):</strong> Invest fixed amounts regularly to reduce timing risk and smooth out volatility</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-green-400 mt-1">•</span>
            <span><strong>Lump Sum:</strong> Invest all at once when you believe the price is favorable—historically outperforms DCA in bull markets</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-green-400 mt-1">•</span>
            <span><strong>Set Realistic Targets:</strong> Base price targets on historical patterns and market analysis, not wishful thinking</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-green-400 mt-1">•</span>
            <span><strong>Risk Management:</strong> Never invest more than you can afford to lose; crypto is highly volatile</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-green-400 mt-1">•</span>
            <span><strong>Take Profits:</strong> Consider taking partial profits at key targets rather than waiting for one exit point</span>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default CryptoInvestmentCalculatorTool;
